import { FunctionComponent, ReactElement } from "react";

import {
    Alert,
    Chip,
    Link as MuiLink,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";

import { InfoOutlined } from "@mui/icons-material";
import { format, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";

import { Paper } from "../../components/common";
import { useAuth, useGetVariantInventory } from "../../sdk/hooks";
import { StockStatus } from "../../sdk/utils/api";
import { AdminRole } from "../../utils/enums";
import { InventoryControls } from "./InventoryControls";

const Header = styled("div")`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};

    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const SectionTitle = styled(Typography)`
    font-size: 18px;
    font-weight: 500;
    color: #626262;
`;

const ChipContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const TableContainer = styled("div")`
    overflow-x: auto;
`;

const CapCell = styled("div")`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const Footer = styled("div")`
    margin-top: ${({ theme }) => theme.spacing(2)};
`;

const StyledAlert = styled(Alert)`
    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const SkuValue = styled("strong")`
    font-weight: 600;
`;

const HeaderCell = styled("div")`
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.5)};
`;

const InfoIcon = styled(InfoOutlined)`
    font-size: 16px;
    color: #9e9e9e;
    cursor: help;
`;

export interface IInventorySectionProps {
    variantId: string;
}

export const InventorySection: FunctionComponent<IInventorySectionProps> = (
    props: IInventorySectionProps,
): ReactElement => {
    const { variantId } = props;

    const { user } = useAuth();
    const inventoryQuery = useGetVariantInventory(variantId);
    const inventory = inventoryQuery.data;

    const canWrite =
        !!user &&
        [AdminRole.ROOT, AdminRole.ADMIN, AdminRole.MANAGER].includes(
            user.role,
        );

    const sharedCount = inventory?.sharedVariantCount ?? 0;

    const quantityTooltip =
        sharedCount > 0
            ? `Synced from EasyEcom and not editable here. This SKU is shared with ${sharedCount} other variant${
                  sharedCount === 1 ? "" : "s"
              }, so the quantity is not this variant's alone.`
            : "Synced from EasyEcom and not editable here.";

    return (
        <Paper>
            <Header>
                <SectionTitle>Inventory</SectionTitle>

                {inventory && (
                    <ChipContainer>
                        <Tooltip
                            enterTouchDelay={0}
                            placement="top"
                            title={
                                inventory.lastChangedAt
                                    ? `Status last changed ${formatDistanceToNow(
                                          new Date(inventory.lastChangedAt),
                                          { addSuffix: true },
                                      )}.`
                                    : "This status has not changed yet."
                            }
                        >
                            <Chip
                                size="small"
                                label={
                                    inventory.status === StockStatus.OOS
                                        ? "Out of stock"
                                        : "In stock"
                                }
                                color={
                                    inventory.status === StockStatus.OOS
                                        ? "error"
                                        : "success"
                                }
                            />
                        </Tooltip>
                        <Tooltip
                            title={
                                inventory.overrideActive
                                    ? "An admin has overridden this status. Automated sync is not controlling it."
                                    : "Status is controlled by the automated inventory sync."
                            }>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={
                                    inventory.overrideActive
                                        ? "Manual"
                                        : "Auto"
                                }
                            />
                        </Tooltip>
                    </ChipContainer>
                )}
            </Header>

            {inventoryQuery.isLoading && <Skeleton height={80} />}

            {inventoryQuery.isError && (
                <Alert severity="error">
                    Could not load inventory for this variant.
                </Alert>
            )}

            {inventory && (
                <>
                    {inventory.stale && (
                        <StyledAlert
                            severity="warning"
                            action={
                                <Tooltip
                                    enterTouchDelay={0}
                                    placement="top"
                                    title={`The last reading is older than the staleness limit of ${inventory.stalenessLimit}s, so automated evaluation is skipping this variant.`}
                                >
                                    <InfoIcon />
                                </Tooltip>
                            }
                        >
                            Stale reading. The status below may be lagging.
                        </StyledAlert>
                    )}

                    {inventory.quantity === null && (
                        <StyledAlert severity="info">
                            No inventory reading has arrived for{" "}
                            <SkuValue>{inventory.variant.sku}</SkuValue> yet.
                        </StyledAlert>
                    )}

                    {inventory.overrideActive && (
                        <StyledAlert
                            severity="info"
                            action={
                                <Tooltip
                                    enterTouchDelay={0}
                                    placement="top"
                                    title={
                                        inventory.status === StockStatus.OOS
                                            ? "The engine will not restock it automatically."
                                            : "The engine will still force it out of stock if quantity falls to the floor cap."
                                    }
                                >
                                    <InfoIcon />
                                </Tooltip>
                            }
                        >
                            {inventory.status === StockStatus.OOS
                                ? "Manually held out of stock."
                                : "Manually held in stock."}
                            {inventory.overrideExpiresAt
                                ? ` Expires ${format(
                                      new Date(inventory.overrideExpiresAt),
                                      "dd MMM yyyy, HH:mm",
                                  )}.`
                                : " Held until an admin releases it."}
                        </StyledAlert>
                    )}

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>SKU</TableCell>
                                    <TableCell align="right">
                                        <HeaderCell>
                                            On hand
                                            <Tooltip
                                                enterTouchDelay={0}
                                                placement="top"
                                                title={quantityTooltip}
                                            >
                                                <InfoIcon />
                                            </Tooltip>
                                        </HeaderCell>
                                    </TableCell>
                                    <TableCell align="right">
                                        Floor cap
                                    </TableCell>
                                    <TableCell align="right">
                                        Restock cap
                                    </TableCell>
                                    <TableCell align="right">
                                        Last synced
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        {inventory.variant.sku}
                                    </TableCell>
                                    <TableCell align="right">
                                        {inventory.quantity ?? "N/A"}
                                    </TableCell>
                                    <TableCell align="right">
                                        <CapCell>
                                            {inventory.floorCap}
                                            {inventory.thresholdsOverridden && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label="Override"
                                                />
                                            )}
                                        </CapCell>
                                    </TableCell>
                                    <TableCell align="right">
                                        {inventory.restockCap}
                                    </TableCell>
                                    <TableCell align="right">
                                        {inventory.syncedAt ? (
                                            <Tooltip
                                                title={format(
                                                    new Date(
                                                        inventory.syncedAt,
                                                    ),
                                                    "dd MMM yyyy, HH:mm:ss",
                                                )}>
                                                <span>
                                                    {formatDistanceToNow(
                                                        new Date(
                                                            inventory.syncedAt,
                                                        ),
                                                        { addSuffix: true },
                                                    )}
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            "Never"
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Footer>
                        <MuiLink
                            component={Link}
                            to={`/inventory/variants/${variantId}/history`}
                            underline="hover">
                            View stock history
                        </MuiLink>
                    </Footer>

                    {canWrite && <InventoryControls inventory={inventory} />}
                </>
            )}
        </Paper>
    );
};
